import React from 'react'
import ShiftCard from '../ShiftCard'
import DailyOpenShiftCard from './DailyOpenShiftCard';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ResizableDailyBreakCard from './ResizeableDailyBreakCard';

const PAID_BREAK_DURATION_HOURS = 10 / 60;

function ResizableDailyShiftCard(props) {
  const [currentShiftInfo, setCurrentShiftInfo] = React.useState(props.shift_info);
  const [appliedTo, setAppliedTo] = React.useState([false, false, false, false, false, false, false]);
  const [dragging, setDragging] = React.useState(null);
  const [dragStart, setDragStart] = React.useState(null);
  const [showBreak, setShowBreak] = React.useState(true)
  const getTotalUnpaidBreakLength = (shiftInfo) =>
    (shiftInfo?.break_length || 0) + (shiftInfo?.break_length_2 || 0);
  const ShiftCardComponent = props.openShift ? DailyOpenShiftCard : ShiftCard;
  // Drawn from the punch when there is one. ShiftCard keeps the real shift,
  // since it resolves its own label; this is only for the geometry and the
  // break markers around it.
  const displayShiftInfo = props.actualGeometry
    ? {
        ...currentShiftInfo,
        shift_start: props.actualGeometry.start,
        shift_length: props.actualGeometry.length,
        shift_end: props.actualGeometry.start + props.actualGeometry.length,
        break_start: props.actualGeometry.break_start,
        break_length: props.actualGeometry.break_length,
        break_start_2: props.actualGeometry.break_start_2,
        break_length_2: props.actualGeometry.break_length_2,
        paid_breaks: props.actualGeometry.paid_breaks,
      }
    : currentShiftInfo;

  // A planned shift has two break slots. A timecard can carry any number, and
  // the hours beside it subtract every one of them, so drawing only the first
  // two paints the rest of the breaks as time worked. The multi-card path in
  // DailySchedule already reads the array; this is the single-card path.
  // Read from displayShiftInfo, which is what breakFitsShift measures against.
  const actualUnpaidBreaks = Array.isArray(displayShiftInfo?.unpaid_breaks)
    ? displayShiftInfo.unpaid_breaks.filter(Boolean)
    : [];

  // Same rule as the multi-shift row: a marker that does not fit inside the
  // drawn shift is not describing it, and would sit on the empty row as a grey
  // block. A minute of slack, since punch times ignore the quarter-hour grid.
  const breakFitsShift = (start, length) => {
    const from = Number(start);
    const span = Number(length);
    if (!Number.isFinite(from) || !Number.isFinite(span) || span <= 0) {
      return false;
    }
    const shiftStart = Number(displayShiftInfo?.shift_start);
    const shiftEnd = Number(displayShiftInfo?.shift_end);
    if (!Number.isFinite(shiftStart) || !Number.isFinite(shiftEnd)) return false;
    const tolerance = 1 / 60;
    return from >= shiftStart - tolerance && from + span <= shiftEnd + tolerance;
  };

  const canResize =
    // Resizing rewrites the planned shift, which actual mode has no business
    // doing from a view of what happened.
    !props.showActualTimes &&
    !props.locked_mode &&
    !props.day_in_past &&
    !props.multipleShifts &&
    !currentShiftInfo?.is_copilot_suggestion;

  const adjustPaidBreaksWithinShift = React.useCallback((shift) => {
    const shiftStart = shift?.shift_start
    const shiftEnd = shift?.shift_end
    const shiftWindow = shiftEnd - shiftStart

    if (!Number.isFinite(shiftStart) || !Number.isFinite(shiftEnd)) {
      return shift?.paid_breaks || []
    }

    if (shiftWindow < PAID_BREAK_DURATION_HOURS) {
      return []
    }

    const normalizedBreaks = (shift?.paid_breaks || [])
      .map((paidBreak) => ({
        ...paidBreak,
        break_duration:
          paidBreak?.break_duration || paidBreak?.break_length || PAID_BREAK_DURATION_HOURS,
        break_start: paidBreak?.break_start ?? shiftStart,
      }))
      .sort((a, b) => a.break_start - b.break_start)

    let occupiedHours = 0
    const trimmedBreaks = []
    for (const paidBreak of normalizedBreaks) {
      if (occupiedHours + paidBreak.break_duration > shiftWindow) break
      occupiedHours += paidBreak.break_duration
      trimmedBreaks.push(paidBreak)
    }

    let currentStart = shiftStart

    return trimmedBreaks.map((paidBreak, index) => {
      const remainingDuration = trimmedBreaks.slice(index).reduce(
        (total, entry) => total + entry.break_duration, 0
      )
      const latestStartForCurrent = shiftEnd - remainingDuration
      const targetStart = Math.max(paidBreak.break_start, currentStart)
      const clampedStart = Math.min(
        Math.max(targetStart, shiftStart),
        latestStartForCurrent
      )

      const break_start = Math.max(clampedStart, currentStart)
      const break_duration = paidBreak.break_duration

      currentStart = break_start + break_duration

      return {
        ...paidBreak,
        break_start,
        break_duration,
      }
    })
  }, [])

  React.useEffect(() => {
    setCurrentShiftInfo(props.shift_info)
  }, [props.shift_info])

  React.useEffect(() => {
    let appliedToCopy = [false, false, false, false, false, false, false];
    appliedToCopy[props.d] = true;
    setAppliedTo(appliedToCopy);
  }, []);

  const handleMouseDown = (e, type) => {
    e.stopPropagation();
    setDragging(type);
    setDragStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (dragging) {
      const sensitivity = 0.012;
      const rawShiftAmount = (e.clientX - dragStart) * sensitivity;
      const shiftAmount = rawShiftAmount;

      setCurrentShiftInfo((prevShiftInfo) => {
        const newShiftInfo = { ...prevShiftInfo };
        if (dragging === 'start') {
          newShiftInfo.shift_start += shiftAmount;
          newShiftInfo.shift_length -= shiftAmount;
        } else if (dragging === 'end') {
          newShiftInfo.shift_end += shiftAmount;
          newShiftInfo.shift_length += shiftAmount;
        } else if (dragging === 'whole') {
          newShiftInfo.shift_start += shiftAmount;
          newShiftInfo.break_start += shiftAmount;
          if ((newShiftInfo.break_length_2 || 0) > 0) {
            newShiftInfo.break_start_2 += shiftAmount;
          }
          newShiftInfo.shift_end += shiftAmount;

          if (Array.isArray(prevShiftInfo?.paid_breaks)) {
            newShiftInfo.paid_breaks = prevShiftInfo.paid_breaks.map(
              (paidBreak) => {
                if (paidBreak?.break_start === undefined) {
                  return { ...paidBreak };
                }

                return {
                  ...paidBreak,
                  break_start: paidBreak.break_start + shiftAmount,
                };
              }
            );
          }
        }

        newShiftInfo.hours = newShiftInfo.shift_length - getTotalUnpaidBreakLength(newShiftInfo);
        newShiftInfo.paid_breaks = adjustPaidBreaksWithinShift(newShiftInfo);

        return newShiftInfo;
      });

      setDragStart(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (dragging) {
      setDragging(null);
      setDragStart(null);

      setCurrentShiftInfo((prevShiftInfo) => {
        const roundToQuarter = (value) => {
          return Math.round(value * 4) / 4;
        };

        const newShiftInfo = { ...prevShiftInfo };
        newShiftInfo.shift_start = roundToQuarter(newShiftInfo.shift_start);
        newShiftInfo.shift_end = roundToQuarter(newShiftInfo.shift_end);
        newShiftInfo.shift_length = newShiftInfo.shift_end - newShiftInfo.shift_start;
        if ((newShiftInfo.break_start < newShiftInfo.shift_start) || ((newShiftInfo.break_start + newShiftInfo.break_length) > newShiftInfo.shift_end)) {
          newShiftInfo.break_start = (((newShiftInfo.shift_end + newShiftInfo.shift_start) / 2) * 4) / 4 - (newShiftInfo.break_length / 2)
        }
        if (
          (newShiftInfo.break_length_2 || 0) > 0 &&
          (
            (newShiftInfo.break_start_2 < newShiftInfo.shift_start) ||
            ((newShiftInfo.break_start_2 + newShiftInfo.break_length_2) > newShiftInfo.shift_end)
          )
        ) {
          const firstBreakEnd = newShiftInfo.break_start + newShiftInfo.break_length;
          const latestSecondBreakStart =
            newShiftInfo.shift_end - newShiftInfo.break_length_2;
          if (latestSecondBreakStart < firstBreakEnd) {
            newShiftInfo.break_start_2 = 0;
            newShiftInfo.break_length_2 = 0;
          } else {
            const targetSecondBreakStart =
              (((newShiftInfo.shift_end - newShiftInfo.shift_start) * 0.75) + newShiftInfo.shift_start) -
              (newShiftInfo.break_length_2 / 2);
            newShiftInfo.break_start_2 = Math.min(
              Math.max(targetSecondBreakStart, firstBreakEnd),
              latestSecondBreakStart
            );
          }
        }
        newShiftInfo.hours = newShiftInfo.shift_length - getTotalUnpaidBreakLength(newShiftInfo);
        newShiftInfo.break_start = roundToQuarter(newShiftInfo.break_start)
        if ((newShiftInfo.break_length_2 || 0) > 0) {
          newShiftInfo.break_start_2 = roundToQuarter(newShiftInfo.break_start_2)
        }
        newShiftInfo.paid_breaks = adjustPaidBreaksWithinShift(newShiftInfo)

        props.addShift(newShiftInfo, 'edit', appliedTo, true);

        return newShiftInfo;
      });
    }
  };

  React.useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, dragStart]);


  return (
    <>
      <div
        style={{
          height: 32,
          // Falls back to the live shift while resizing, which only happens
          // outside actual mode.
          width:
            (props.actualGeometry?.length ?? currentShiftInfo?.shift_length) *
              props.hour_increment +
            "%",
          marginLeft:
            ((props.actualGeometry?.start ?? currentShiftInfo?.shift_start) -
              props.previousEndTime) *
              props.hour_increment +
            "%",
          zIndex: 9,
          position: "relative",
          cursor: 'default'
        }}
      >
        {canResize && (
          <>
            <div
              data-testid="shift-resize-start"
              onMouseDown={(e) => handleMouseDown(e, 'start')}
              style={{
                position: 'absolute',
                left: -5,
                top: 0,
                bottom: 0,
                width: 10,
                cursor: 'ew-resize',
                zIndex: 8
              }}
            />
            <div
              data-testid="shift-resize-end"
              onMouseDown={(e) => handleMouseDown(e, 'end')}
              style={{
                position: 'absolute',
                right: -5,
                top: 0,
                bottom: 0,
                width: 10,
                cursor: 'ew-resize',
                zIndex: 8
              }}
            />
            {showBreak &&
              <div
                data-testid="shift-drag-whole"
                className="text-white"
                style={{
                  position: 'absolute',
                  right: 20,
                  top: 2,
                  bottom: 0,
                  width: 20,
                  height: 20,
                  zIndex: 9999,
                  cursor: 'move'
                }}
                onMouseDown={(e) => handleMouseDown(e, 'whole')}
              >
                <DragIndicatorIcon />
              </div>
            }
          </>
        )}
        <ShiftCardComponent
          {...props}
          shift_info={currentShiftInfo}
          setShowBreak={setShowBreak}
          isCopilotOn={props.isCopilotOn}
        />
      </div>
      {displayShiftInfo?.paid_breaks?.map((paidBreak, paidBreakIndex) => {
        const paidBreakDuration =
          paidBreak?.break_duration || paidBreak?.break_length || 0;

        if (
          paidBreak?.break_start === undefined ||
          paidBreakDuration <= 0
        ) {
          return null;
        }

        return (
          <div
            key={`paid-break-${paidBreakIndex}-${paidBreak.break_start}`}
            className="absolute bg-emerald-200 border border-emerald-700 h-3 rounded-sm pointer-events-none"
            style={{
              width: paidBreakDuration * props.hour_increment + "%",
              marginLeft:
                (paidBreak.break_start - props.previousEndTime) *
                  props.hour_increment +
                15 +
                "%",
              top: "calc(50% - 16px)",
              borderTop: "none",
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              zIndex: 11,
            }}
          ></div>
        );
      })}
      {props.showActualTimes ? (
        showBreak &&
        actualUnpaidBreaks.map((entry, index) => {
          if (!breakFitsShift(entry?.break_start, entry?.break_length)) {
            return null;
          }
          const previous = actualUnpaidBreaks[index - 1];
          const next = actualUnpaidBreaks[index + 1];
          return (
            <ResizableDailyBreakCard
              key={`actual-break-${index}-${entry?.break_start}`}
              shift_info={{
                ...displayShiftInfo,
                break_start: entry.break_start,
                break_length: entry.break_length,
              }}
              day_in_past={props.day_in_past}
              locked_mode={props.locked_mode}
              showActualTimes={props.showActualTimes}
              multipleShifts={props.multipleShifts}
              hour_increment={props.hour_increment}
              previousEndTime={props.previousEndTime}
              d={props.d}
              addShift={props.addShift}
              minStart={
                previous
                  ? previous.break_start + previous.break_length
                  : undefined
              }
              maxEnd={next ? next.break_start : displayShiftInfo.shift_end}
            />
          );
        })
      ) : (
        <>
        {breakFitsShift(displayShiftInfo.break_start, displayShiftInfo.break_length) &&
          showBreak && (
          <ResizableDailyBreakCard
            shift_info={displayShiftInfo}
            day_in_past={props.day_in_past}
            locked_mode={props.locked_mode}
            showActualTimes={props.showActualTimes}
            multipleShifts={props.multipleShifts}
            hour_increment={props.hour_increment}
            previousEndTime={props.previousEndTime}
            d={props.d}
            addShift={props.addShift}
            maxEnd={
              (displayShiftInfo.break_length_2 || 0) > 0
                ? displayShiftInfo.break_start_2
                : displayShiftInfo.shift_end
            }
          />
        )}
        {breakFitsShift(
          displayShiftInfo.break_start_2,
          displayShiftInfo.break_length_2
        ) &&
          showBreak && (
          <ResizableDailyBreakCard
            shift_info={displayShiftInfo}
            day_in_past={props.day_in_past}
            locked_mode={props.locked_mode}
            showActualTimes={props.showActualTimes}
            multipleShifts={props.multipleShifts}
            hour_increment={props.hour_increment}
            previousEndTime={props.previousEndTime}
            d={props.d}
            addShift={props.addShift}
            breakField="break_start_2"
            breakLengthField="break_length_2"
            minStart={displayShiftInfo.break_start + displayShiftInfo.break_length}
          />
        )}
        </>
      )}
    </>
  )
}

export default ResizableDailyShiftCard
